// @flow
import * as React from 'react';

import { connect, useDispatch } from 'react-redux';

import { Link } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createFilter } from 'react-select';

import { useEntityPermissions } from '@gandi/acl';
import { selectIsSamlUser } from '@gandi/authentication';
import {
  selectCartUUID as selectCartId,
  selectCartProductOwner,
  setCartProductOwner,
  setCartReselleeId,
  setCartOwnerIsReseller,
} from '@gandi/cart';
import { CountryName } from '@gandi/react-places';
import {
  type Organization,
  fetchOrganizationsList as fetchCandidates,
  fetchOneOrganization as fetchCandidate,
  selectOrganizationsList,
  selectIsFetchingOrganizations,
  selectIsFailureOrganizations,
  selectIsLoadedOrganizations,
  selectIsToRefetchOrganizations,
} from '@gandi/redux-organization';
import { selectCurrentId } from '@gandi/redux-sharing';

import { type TranslatorProps, useTranslator } from '@gandi/translate';
import { Base, Text, Badge, Svg } from '@gandi/ui';
import { Custom } from '@gandi/ui-select';

import {
  selectShopCurrent,
  selectHasMissingOrgInfo,
} from '../../../modules/organization/selectors';
import { ORGA_WRITE } from '../../../modules/permissions';

import type {
  ComponentWithPrefetch,
  FullState,
  PreFetchArgs,
} from '../../../types';

import styles from './PayerSelect.scss';

type OwnProps = {
  frozen: boolean,
};

type StateProps = {
  cartId: string,
  currentId: string,
  missingOrgInfo: boolean,
  currentOrg: Organization,
  candidates: Array<Organization>,
  loadedCandidates: boolean,
  failureCandidates: boolean,
  fetchingCandidates: boolean,
  refetchChandidates: boolean,
  productOwner: string,
  isSamlUser: boolean,
};

export type Props = {
  ...OwnProps,
  ...StateProps,
};

const SVGStyle = { height: '1rem', width: 'auto' };
const textWrapStyle = { whiteSpace: 'normal' };

function stringify({ data }: any): string {
  return Object.keys(data)
    .filter(key => data[key] && typeof data[key] === 'string')
    .reduce((acc, key) => {
      // default case
      let substring = data[key];

      // Handle firstname / lastname semantic groups
      if (key === 'firstname') {
        substring = `${acc}${data.firstname} ${data.lastname}`;
      }
      if (key === 'lastname') {
        substring = `${acc}${data.lastname} ${data.firstname}`;
      }

      return `${acc}${substring} `;
    }, '');
}

function getOrgEdit(org: string, cart: string) {
  return `/cart/${cart}/configure/account/${org}?owner=products`;
}

function getId(org: any): string {
  return org.id || '';
}

function getTypeLabel(__: TranslatorProps['__'], type: Organization['type']) {
  if (type === 'individual') return __('Individual');
  if (type === 'company') return __('Company');
  if (type === 'association') return __('Association');
  if (type === 'publicbody') return __('Public Body');
  return __('Organization');
}

function getLegalName(__: TranslatorProps['__'], org: Organization): string {
  let name =
    org.firstname && org.lastname ? `${org.firstname} ${org.lastname}` : '';
  if (org.type !== 'individual') {
    const legal = org.orgname || org.name || '';
    name = name ? `${legal} / ${name}` : legal;
  }
  return name;
}

function getDisplayName(__: TranslatorProps['__'], org: Organization): string {
  return org.name || org.orgname || '';
}

function renderOption(
  __: TranslatorProps['__'],
  missingOrgInfo: boolean,
  current: string,
  productOwner: string,
  org: Organization,
): React.MixedElement {
  const legalName = getLegalName(__, org);

  return (
    <Base padding="small" key={`owner-${org.id}`}>
      <Base tagName="p" margin="none">
        <Base flex flexAlignItemsCenter tagName="span">
          <Text weight="semibold">{getDisplayName(__, org)}</Text>
          {
            // $FlowFixMe only available when org: CartOwnerCandidate
            !!org.personal && <Badge className={styles.badge}>{__('Me')}</Badge>
          }
          {org.reseller && (
            <Svg
              className={styles.badge}
              title={__('Reseller')}
              name="badgeReseller"
              theme="anthracite"
              style={SVGStyle}
            />
          )}
          {org.corporate && (
            <Svg
              className={styles.badge}
              title={__('Corporate')}
              name="badgeCorporate"
              theme="anthracite"
              style={SVGStyle}
            />
          )}
        </Base>
        <Text color="light" size="small" transform="uppercase">
          {getTypeLabel(__, org.type)}
        </Text>
        {legalName && (
          <>
            <br />
            <span>{legalName}</span>
          </>
        )}
        {org.email && (
          <>
            <br />
            <span>{org.email}</span>
          </>
        )}
        {org.country && org.city && org.streetaddr && (
          <>
            <br />
            <span style={textWrapStyle}>
              {`${org.streetaddr}${org.zip ? `, ${org.zip}` : ''}, ${
                org.city
              }, `}
              <CountryName country={org.country} />
            </span>
          </>
        )}
        {missingOrgInfo && org.id === productOwner && org.id !== current && (
          <>
            <br />
            <Text color="success">
              {__('Required information will be asked afterwards')}
            </Text>
          </>
        )}
      </Base>
    </Base>
  );
}

function onChange(
  callback: (id: string) => void,
  current: void | Organization,
  update: (next: void | Organization) => any,
  next_: any,
) {
  const next = Array.isArray(next_) ? next_[0] : next_;
  if (next !== current) {
    update(next);
    if (typeof callback === 'function') {
      callback(next.id);
    }
  }
}

export function OwnerSelect({
  cartId,
  currentOrg,
  currentId,
  candidates,
  frozen,
  missingOrgInfo,
  loadedCandidates,
  fetchingCandidates,
  failureCandidates,
  refetchChandidates,
  productOwner,
  isSamlUser,
}: Props): React.Node {
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();

  const [mounted, setMounted] = React.useState(false);

  /* --------------- Force a re-render after hydration/mount. --------------- */
  // Required to mitigate a SSR/hydration bug in Safari
  React.useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted, setMounted]);
  /* ------------------------------------------------------------------------ */

  /* ----------- Reset possibly conflicting cart ownership state. ----------- */
  // Required to prevent possible desync cases when user selects a product owner
  // for the cart, then change their mind a select a customer.
  React.useEffect(() => {
    dispatch(setCartReselleeId(null));
  }, [dispatch]);
  /* ------------------------------------------------------------------------ */

  React.useEffect(() => {
    dispatch(setCartOwnerIsReseller(null));
  }, [dispatch]);

  const defaultOwner = productOwner
    ? candidates.find(candidate => candidate.id === productOwner) || undefined
    : currentOrg;

  const [owner, set] = React.useState(defaultOwner);
  const [key, setKey] = React.useState(
    defaultOwner ? defaultOwner.id : 'not-loaded',
  );

  const { hasPermissions, isLoadedPermissions, isFetchingPermissions } =
    useEntityPermissions(owner?.id || 'no-owner-yet');

  const shouldFetch =
    !fetchingCandidates &&
    ((!loadedCandidates && !failureCandidates) || refetchChandidates);

  React.useEffect(() => {
    if (shouldFetch) {
      dispatch(fetchCandidates()).then(response => {
        if (
          Array.isArray(response.data) &&
          productOwner &&
          defaultOwner?.id !== productOwner
        ) {
          const match = response.data.find(
            candidate => candidate.id === productOwner,
          );
          if (match) {
            set(match);
            setKey(match.id);
          }
        }
      });
    }
    // Only dependency change that should trigger this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = React.useCallback(
    onChange.bind(
      null,
      (orgId: string) => {
        dispatch(setCartProductOwner(orgId));
        dispatch(fetchCandidate(orgId));
      },
      owner,
      set,
    ),
    [owner, set],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderOpt = React.useCallback(
    // $FlowFixMe[incompatible-call]
    renderOption.bind(null, __, missingOrgInfo, currentOrg, productOwner),
    [__, currentId, missingOrgInfo, currentOrg, productOwner],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterOpt = React.useCallback(createFilter({ stringify }), []);

  const renderNoOpt = React.useCallback(
    () => __('Only organizations eligible for this order are listed'),
    [__],
  );

  return (
    <Base flex flexCol>
      <div className={styles.container}>
        <Custom
          key={key}
          id="owner-select-container"
          inputId="owner-select-input"
          instanceId="owner-select-instance"
          translator={translator}
          name="owner"
          isClearable={false}
          isDisabled={!mounted || frozen || fetchingCandidates}
          value={defaultOwner}
          options={candidates}
          getOptionValue={getId}
          filterOption={filterOpt}
          renderOption={renderOpt}
          onChange={handleChange}
          maxMenuHeight={500}
          noOptionsMessage={renderNoOpt}
        />
      </div>
      <span className={styles.linksContainer}>
        {owner &&
          !isFetchingPermissions &&
          isLoadedPermissions &&
          hasPermissions([ORGA_WRITE]) && (
            <>
              <Link
                to={getOrgEdit(owner.id, cartId)}
                className={styles.editLink}
              >
                {translator.__('Edit %(name)s', {
                  name: getDisplayName(__, owner),
                })}
              </Link>
              {!isSamlUser && <hr className={styles.separator} />}
            </>
          )}
        {!isSamlUser && (
          <Link to={`/cart/${cartId}/configure/account/new?owner=products`}>
            {__('Create New')}
          </Link>
        )}
      </span>
    </Base>
  );
}

OwnerSelect.preFetch = ({
  dispatch,
}: PreFetchArgs<{}, any, any, FullState>): Promise<any> =>
  dispatch(fetchCandidates());

const mapStateToProps = (state: FullState): StateProps => {
  const cartId = selectCartId(state);
  const currentId = selectCurrentId(state);
  const currentOrg = selectShopCurrent(state, true);
  const candidates = selectOrganizationsList(state);
  const loadedCandidates = selectIsLoadedOrganizations(state);
  const fetchingCandidates = selectIsFetchingOrganizations(state);
  const failureCandidates = selectIsFailureOrganizations(state);
  const refetchChandidates = selectIsToRefetchOrganizations(state);
  const productOwner = selectCartProductOwner(state);
  const missingOrgInfo = !!selectHasMissingOrgInfo(state, productOwner);
  const isSamlUser = selectIsSamlUser(state);

  // $FlowFixMe[prop-missing]: compat type Organization
  return {
    cartId,
    currentId,
    missingOrgInfo,
    currentOrg,
    candidates,
    loadedCandidates,
    fetchingCandidates,
    failureCandidates,
    refetchChandidates,
    productOwner,
    isSamlUser,
  };
};

// $FlowFixMe[underconstrained-implicit-instantiation]
export default (connect(mapStateToProps)(OwnerSelect): ComponentWithPrefetch<
  OwnProps,
  typeof OwnerSelect.preFetch,
>);
